import type { FC } from 'react';
import React, { useCallback } from 'react';
import styled from 'styled-components';
import type { RouteComponentProps } from 'react-router-dom';
import { withRouter } from 'react-router-dom';
import type { RangeDebugResponse } from 'venn-api';
import type { AnalysesPeriod, AnalysisSubject, TimeFrame } from 'venn-utils';
import {
  assert,
  LibraryItemType,
  MANAGE_DATA_SECTION,
  navigateToLibrary,
  Routes,
  shouldUserUpdateDataForFund,
} from 'venn-utils';
import FailedAnalysisInfo from '../../chart-errors/FailedAnalysisInfo';
import type { TrackAnalysisProps } from '../../chart-errors/TrackAnalysis';
import type { RangeDebugGroup } from '../../../hooks';
import { useRangesDebug } from '../../../hooks';
import { Button, Loading } from 'venn-ui-kit';
import { getMaxFrequency } from '../../chart-errors/PortfolioRangesUtils';
import { compact, isNil } from 'lodash';
import ErrorInvalidAnalysisPeriod from '../../chart-errors/shared-errors/ErrorInvalidAnalysisPeriod';
import type { FailedAnalysisError } from '../../chart-errors/types';
import BulkEmptyState from '../../../bulk-empty-state/BulkEmptyState';
import { ManageDataLink } from '../../../manage-data';

interface CorrelationErrorProps extends RouteComponentProps {
  subject?: AnalysisSubject;
  error: FailedAnalysisError;
  isMissingMaster: boolean;
  actualTimeFrame: TimeFrame;
  categoryActive: boolean;
  trackingProps: TrackAnalysisProps;
  onResetTimeFrame?: () => void;
}

const CorrelationError: FC<React.PropsWithChildren<CorrelationErrorProps>> = ({
  history,
  subject,
  error,
  isMissingMaster,
  actualTimeFrame,
  categoryActive,
  trackingProps,
  onResetTimeFrame,
}) => {
  const DEFAULT_HEADER_MESSAGE = 'Unable to run analysis';

  const redirectToCreateNewPortfolio = useCallback(() => {
    history.push(Routes.CREATE_PORTFOLIO);
  }, [history]);

  const redirectToMasterHoldings = useCallback(() => {
    navigateToLibrary(history, {
      selectedFilters: {
        assetTypes: [],
        itemType: LibraryItemType.MASTER_PORTFOLIO_HOLDING,
        quickFilters: [],
        tags: [],
      },
    });
  }, [history]);

  const { loading, masterRangeDebug, rangeDebugGroup } = useRangesDebug(
    subject,
    categoryActive,
    trackingProps.trackingId,
    true,
  );

  if (!subject) {
    return (
      <EmptyMasterWithCTA>
        <BulkEmptyState header={DEFAULT_HEADER_MESSAGE} message={error.message} />
      </EmptyMasterWithCTA>
    );
  }

  if (subject.type === 'investment' && isMissingMaster) {
    return (
      <EmptyMasterWithCTA>
        <BulkEmptyState
          header="Set up your portfolio to view correlations"
          message={error.message}
          actionLabel="Set Up Your Portfolio"
          onAction={redirectToCreateNewPortfolio}
        />
      </EmptyMasterWithCTA>
    );
  }

  // TODO(houssein): Refactor ManageDataLink to a custom navigation hook
  // @ts-expect-error: fixme
  const customRenderer = (onClick) => (
    <div>
      <p>
        Go to{' '}
        <button type="button" onClick={onClick} className="text-venn-dark-blue hover:text-venn-dark-blue/90 font-bold">
          Manage Data
        </button>{' '}
        to apply proxies.
      </p>
      <Button className="print:hidden" onClick={onClick} dominant refreshedStyling>
        Manage Data
      </Button>
    </div>
  );

  const isMasterConstraining = getIsMasterConstraining(masterRangeDebug, rangeDebugGroup);
  const isFundOutOfDate = shouldUserUpdateDataForFund(subject.fund);
  const isPeriodManuallyConstrained = isAnalysisPeriodManuallyConstrained(
    actualTimeFrame,
    trackingProps?.analysesPeriod,
  );
  const canInterpolate = subject.fund?.interpolatable;

  if (
    subject.type === 'investment' &&
    subject.fund &&
    (isFundOutOfDate || isMasterConstraining || isPeriodManuallyConstrained || canInterpolate || loading)
  ) {
    const showViewMasterAction =
      !loading &&
      !isFundOutOfDate &&
      !isPeriodManuallyConstrained &&
      !canInterpolate &&
      isMasterConstraining &&
      !(subject.superType === 'portfolio' && subject.portfolio?.master);
    return (
      <EmptyMasterWithCTA>
        <BulkEmptyState
          header={DEFAULT_HEADER_MESSAGE}
          message={
            loading ? (
              <Loading title="Loading details..." />
            ) : isPeriodManuallyConstrained ? (
              <ErrorInvalidAnalysisPeriod
                defaultMessage="Your selected analysis period may be constraining correlation analysis from running."
                onResetAnalysisPeriod={onResetTimeFrame}
              />
            ) : (
              <>
                {error.message}
                <ManageDataLink
                  subject={subject}
                  defaultEditData
                  customRenderer={customRenderer}
                  forceSection={MANAGE_DATA_SECTION.ANALYSIS_RANGE}
                  errorState
                />
              </>
            )
          }
          actionLabel={showViewMasterAction ? 'View Master Holdings' : undefined}
          onAction={showViewMasterAction ? redirectToMasterHoldings : undefined}
        />
      </EmptyMasterWithCTA>
    );
  }

  return (
    <FailedAnalysisInfo
      subject={subject}
      relativeToBenchmark={trackingProps.relativeToBenchmark}
      categoryActive={categoryActive}
      analysesPeriod={trackingProps.analysesPeriod}
      error={error}
      regressionName="Correlation"
      onResetAnalysisPeriod={onResetTimeFrame}
      trackingId={trackingProps.trackingId}
      updateAnalysisStatusForTracking={trackingProps.updateAnalysisStatusForTracking}
      blockNames={trackingProps.blockNames}
      blockTitles={trackingProps.blockTitles}
    />
  );
};

export default withRouter(CorrelationError);

const EmptyMasterWithCTA = styled.div`
  margin: 20px;
`;

function getIsMasterConstraining(masterRangeDebug?: RangeDebugResponse, rangeDebugGroup?: RangeDebugGroup): boolean {
  const masterConstrainingRanges =
    masterRangeDebug?.constrainingPrimaryStart || masterRangeDebug?.constrainingPrimaryEnd;
  const masterConstrainingFrequency = isMasterConstrainingFrequency(masterRangeDebug, rangeDebugGroup);
  return masterConstrainingRanges || masterConstrainingFrequency || !!masterRangeDebug?.insufficientRange;
}

function isMasterConstrainingFrequency(
  masterRangeDebug?: RangeDebugResponse,
  rangeDebugGroup?: RangeDebugGroup,
): boolean {
  if (!masterRangeDebug || !rangeDebugGroup) {
    return false;
  }
  if (!masterRangeDebug.constrainingPrimaryFrequency) {
    return false;
  }

  const analysisSubjectsFrequency = getMaxFrequency(
    compact([rangeDebugGroup.primary.response?.maxFrequency, rangeDebugGroup.benchmark?.response?.maxFrequency]),
  );
  const masterFrequency = masterRangeDebug?.maxFrequency;

  return (
    (analysisSubjectsFrequency === 'DAILY' || analysisSubjectsFrequency === 'MONTHLY') &&
    masterFrequency !== 'DAILY' &&
    masterFrequency !== 'MONTHLY'
  );
}

function isAnalysisPeriodManuallyConstrained(timeFrame: TimeFrame, analysesPeriod?: AnalysesPeriod): boolean {
  const { startTime, endTime } = timeFrame;
  if (!analysesPeriod || (startTime === undefined && endTime === undefined)) {
    return false;
  }

  const { maxStartTime, maxEndTime } = analysesPeriod;
  if (isNil(maxStartTime) && isNil(maxEndTime)) {
    return false;
  }

  if (isNil(maxStartTime)) {
    assert(maxEndTime !== undefined);
    return !isNil(endTime) && endTime < maxEndTime;
  }

  if (isNil(maxEndTime)) {
    assert(maxStartTime !== undefined);
    return !isNil(startTime) && maxStartTime < startTime;
  }

  return (!isNil(startTime) && maxStartTime < startTime) || (!isNil(endTime) && endTime < maxEndTime);
}
